(() => {
  "use strict";

  const BOX_VARIANT = 59549897949470;
  const WRAPPING_VARIANT = 59524529258782;
  const OTHER_OPTION_VARIANT = 50744423776542;
  const excludedVariants = new Set([BOX_VARIANT, WRAPPING_VARIANT, OTHER_OPTION_VARIANT]);
  const root = window.Shopify?.routes?.root || "/";
  const rejectedKey = "misy-gift-box-rejected-cart";
  let syncing = false;
  let needsResync = false;
  let internallyRefreshing = false;

  function isGiftable(item) {
    const type = (item.product_type || "").trim().toLowerCase();
    const handle = (item.handle || "").toLowerCase();
    return !excludedVariants.has(item.variant_id) && !item.gift_card && type !== "gift option" && type !== "option" && type !== "gift card" && !handle.startsWith("gift-card");
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, { credentials: "same-origin", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Cart ${response.status}`);
    return response.json();
  }

  async function postCart(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || `${path} ${response.status}`);
    return data;
  }

  function refreshCart() {
    if (window.themeVariables?.settings?.pageType === "cart") {
      window.location.reload();
    } else {
      internallyRefreshing = true;
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      internallyRefreshing = false;
    }
  }

  function cartSignature(cart) {
    const lines = cart.items.filter((item) => item.variant_id !== BOX_VARIANT)
      .map((item) => `${item.variant_id}:${item.quantity}:${JSON.stringify(item.properties || {})}`).sort();
    return JSON.stringify([lines, cart.discount_codes || []]);
  }

  function rejectedSignature() {
    try { return window.sessionStorage.getItem(rejectedKey); } catch (_) { return null; }
  }

  function setRejectedSignature(value) {
    try {
      if (value) window.sessionStorage.setItem(rejectedKey, value);
      else window.sessionStorage.removeItem(rejectedKey);
    } catch (_) { /* Storage is optional. */ }
  }

  async function syncGiftBox(cart) {
    if (syncing) { needsResync = true; return; }
    if (!Array.isArray(cart?.items)) return;
    const giftableCount = cart.items.reduce((count, item) => count + (isGiftable(item) ? item.quantity : 0), 0);
    const box = cart.items.find((item) => item.variant_id === BOX_VARIANT);
    const autoBox = cart.items.find((item) => item.variant_id === BOX_VARIANT && item.properties?._auto_gift === "true");
    const wrapping = cart.items.find((item) => item.variant_id === WRAPPING_VARIANT);
    const signature = cartSignature(cart);

    let path;
    let body;
    if (autoBox && (giftableCount < 2 || wrapping)) {
      path = "cart/change.js";
      body = { id: autoBox.key, quantity: 0 };
    } else if (autoBox && autoBox.quantity > 1) {
      path = "cart/change.js";
      body = { id: autoBox.key, quantity: 1 };
    } else if (autoBox && autoBox.final_line_price > 0) {
      setRejectedSignature(signature);
      path = "cart/change.js";
      body = { id: autoBox.key, quantity: 0 };
    } else if (giftableCount >= 2 && !wrapping && !box && rejectedSignature() !== signature) {
      path = "cart/add.js";
      body = { items: [{ id: BOX_VARIANT, quantity: 1, properties: { _auto_gift: "true" } }] };
    }
    if (!path) return;

    syncing = true;
    try {
      await postCart(path, body);
      if (path === "cart/add.js" || body.quantity === 1) {
        const confirmedCart = await getCart();
        const confirmedBox = confirmedCart.items.find((item) => item.variant_id === BOX_VARIANT && item.properties?._auto_gift === "true");
        const confirmedWrapping = confirmedCart.items.some((item) => item.variant_id === WRAPPING_VARIANT);
        if (confirmedBox && (confirmedBox.final_line_price > 0 || confirmedWrapping)) {
          if (confirmedBox.final_line_price > 0 && !confirmedWrapping) setRejectedSignature(signature);
          await postCart("cart/change.js", { id: confirmedBox.key, quantity: 0 });
        } else if (confirmedBox) {
          setRejectedSignature(null);
        } else {
          setRejectedSignature(signature);
        }
      }
      refreshCart();
    } catch (error) {
      console.warn("Misy gift BOX could not be updated", error);
    } finally {
      syncing = false;
      if (needsResync) {
        needsResync = false;
        void getCart().then(syncGiftBox).catch(() => {});
      }
    }
  }

  async function addWrapping(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isInteger(variantId) || variantId !== WRAPPING_VARIANT) return;
    const errorMessage = button.closest(".misy-cart-gift-upsell")?.querySelector("[data-misy-wrapping-error]");
    if (errorMessage) { errorMessage.hidden = true; errorMessage.textContent = ""; }
    button.setAttribute("aria-busy", "true");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const cart = await getCart();
      const autoBoxes = cart.items.filter((item) => item.variant_id === BOX_VARIANT && item.properties?._auto_gift === "true");
      for (const item of autoBoxes) await postCart("cart/change.js", { id: item.key, quantity: 0 });

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await postCart("cart/add.js", {
        items: [{ id: variantId, quantity: 1, properties: { "対象商品": "カート内商品" } }],
        sections: sections.join(","),
        sections_url: `${root}cart`
      });

      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
      } else {
        const updatedCart = await getCart();
        updatedCart.sections = response.sections;
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", onSuccessDo: "force_open_drawer", cart: updatedCart }
        }));
      }
    } catch (error) {
      if (errorMessage) {
        errorMessage.hidden = false;
        errorMessage.textContent = "追加内容を確認できませんでした。カートを更新してご確認ください。";
      }
      try { await syncGiftBox(await getCart()); } catch (_) { /* Keep the cart available if refresh fails. */ }
    } finally {
      button.removeAttribute("aria-busy");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }

  document.addEventListener("cart:change", (event) => { void syncGiftBox(event.detail?.cart); });
  document.addEventListener("cart:refresh", () => {
    if (!internallyRefreshing) void getCart().then(syncGiftBox).catch(() => {});
  });
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-misy-wrapping-add]");
    if (button) { event.preventDefault(); void addWrapping(button); }
  });
  const initialize = () => { void getCart().then(syncGiftBox).catch(() => {}); };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();
})();
